/**
 * LifecycleStepRail Component
 *
 * Compact step rail shown above the settings of the open step.
 */
import { Fragment } from 'react';
import { cn, Icon } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import {
  CREATION_ICON,
  isStepOff,
  stepLabelKey,
  type LifecycleDocument,
  type LifecycleStep,
  type LifecycleStepKey
} from './types';

export interface LifecycleStepRailProps {
  /** Document whose lifecycle is drawn. */
  document: LifecycleDocument;
  /** Steps of the lifecycle with their automation state. */
  steps: LifecycleStep[];
  /** Step whose settings are open. */
  openStep: LifecycleStepKey;
  /** Opens the settings of another step. */
  onSelectStep: (step: LifecycleStepKey) => void;
  /** Id of the heading labelling the rail. */
  labelId: string;
}

/**
 * Step icon with its state pinned to the badge — a lock on locked steps, a
 * status dot otherwise. Keeps the state out of the text column.
 */
function StepBadge({ step, isOpen }: { step: LifecycleStep; isOpen: boolean }) {
  return (
    <span className='relative flex-none'>
      <span
        className={cn(
          'flex w-9 h-9 items-center justify-center rounded-lg',
          isOpen ? 'bg-background-base' : 'bg-background-input'
        )}
      >
        <Icon
          name={step.icon}
          className={cn(
            'w-5 h-5',
            isOpen
              ? 'text-foreground-base'
              : isStepOff(step)
                ? 'text-foreground-disabled'
                : 'text-foreground-muted'
          )}
        />
      </span>
      <span
        className={cn(
          'absolute -bottom-1 -right-1 flex w-4 h-4 items-center justify-center rounded-full',
          isOpen ? 'bg-background-input' : 'bg-background-base'
        )}
      >
        {step.isLocked ? (
          <Icon name='lock' className='w-2.5 h-2.5 text-foreground-disabled' />
        ) : (
          <span
            className={cn(
              'w-2 h-2 rounded-full',
              step.isEnabled ? 'bg-success-border' : 'bg-foreground-disabled'
            )}
          />
        )}
      </span>
    </span>
  );
}

export function LifecycleStepRail({
  document,
  steps,
  openStep,
  onSelectStep,
  labelId
}: LifecycleStepRailProps) {
  const t = useFeatureTranslation('systemSettings.automation.lifecycle');
  const label = (step: LifecycleStep, field: string) =>
    t(`steps.${stepLabelKey(document, step.key)}.${field}`);

  return (
    <div className='overflow-x-auto rounded-xl bg-background-base p-sm'>
      <nav aria-labelledby={labelId} className='flex w-full min-w-max items-center gap-xs'>
        <span
          className='flex w-9 h-9 flex-none items-center justify-center rounded-full bg-background-input text-foreground-disabled'
          title={t(`steps.${document}Input`)}
        >
          <Icon name={CREATION_ICON[document]} className='w-5 h-5' />
        </span>

        {steps.map(step => {
          const isOpen = step.key === openStep;

          return (
            <Fragment key={step.key}>
              <Icon
                name='chevron-down'
                rotate={270}
                aria-hidden='true'
                className='w-4 h-4 flex-none text-foreground-disabled'
              />
              <button
                type='button'
                aria-current={isOpen ? 'true' : undefined}
                title={label(step, 'description')}
                onClick={() => onSelectStep(step.key)}
                data-testid={`lifecycle-${document}-step-${step.key}`}
                className={cn(
                  'flex flex-1 min-w-max items-center justify-start gap-md rounded-xl px-md py-sm text-left transition-colors cursor-pointer',
                  isOpen
                    ? 'bg-background-input border border-border-input shadow-sm'
                    : 'border border-transparent hover:bg-background-muted',
                  isStepOff(step) && 'border-dashed',
                  isStepOff(step) && !isOpen && 'border-border-base'
                )}
              >
                <StepBadge step={step} isOpen={isOpen} />
                <span className='flex flex-col items-start gap-2xs'>
                  <span
                    className={cn(
                      'text-sm font-semibold whitespace-nowrap',
                      isOpen
                        ? 'text-foreground-base'
                        : isStepOff(step)
                          ? 'text-foreground-disabled'
                          : 'text-foreground-muted'
                    )}
                  >
                    {label(step, 'short')}
                  </span>
                  <span
                    className={cn(
                      'text-xs font-medium whitespace-nowrap',
                      step.isLocked && 'text-foreground-disabled',
                      !step.isLocked &&
                        (step.isEnabled ? 'text-success-text' : 'text-foreground-disabled')
                    )}
                  >
                    {step.stateLabel}
                  </span>
                </span>
              </button>
            </Fragment>
          );
        })}
      </nav>
    </div>
  );
}
